/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

/*
 * TicketEditComponent is the top level component for editing tickets.
 * This is an abstract class (no template), specific ticket types (task,
 * constraint, milestone) inherent from this class to implement a
 * template and any type specific behaviors.
 */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { combineLatest, EMPTY, merge, Observable, Subject } from 'rxjs';
import {
	distinctUntilChanged,
	map,
	pluck,
	skipUntil,
	startWith,
	take,
	takeUntil,
	pairwise,
	skip,
	tap,
} from 'rxjs/operators';
import { pluckList } from 'src/app/plain-javascript/services/rxjs-internal-extensions';
import {
	dateRangeStartRequiredValidator,
	greaterThanValidator,
	requiredOrMulti,
	validatorDecorator,
} from '../ticket-errors';
import { VarianceFormData, varianceFormDataValidator } from './variance-prompt/variance-prompt.component';
import {
	CONSTRAINT_ACCEPTANCE_STATE as CAP,
	TicketType as TT,
	ActualPhase,
	ActualPhase as AP,
	Side,
	ProjectMember,
	PlanAccessLevel,
} from '@touchplan/common';
import { ConstraintFormControlStates as CFCS } from './constraint-form-control-states';
import { EditOverlay, TicketEditService } from '../ticket-edit.service';
import { PlanState } from 'src/app/services/plan-state.service';
import { EditCombinedData, EditTicketsCombined } from 'src/app/app-data/models/EditTicketsCombined';
import { ConstraintAcceptanceService } from 'src/app/modules/ticket-edit/constraint-acceptance.service';
import * as AppStrings from '@touchplan/strings';
import { getNested } from 'utils/basic-utils';
import { PlanAccessCheck } from 'src/app/services/PlanAccessCheck';
import {
	activeLineSideValidator,
	ActiveLineValidatorConfig,
} from 'src/app/app-data/validators/active-line-side.validator';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import ticketValidator from '@touchplan/config/lib/ticket-validators.json';
import { DateFormControlAdapter } from 'src/app/common/docked-ticket/services/ticket-form-manager/DateFormControlAdapter';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { ILoggerService } from '@touchplan/logging';

// tslint:disable member-ordering

/**
 * This object maps which form control should be disabled based on the ticket type and the actual phase
 */
const FormControlsToDisable = {
	[TT.Task]: {
		// These are sorted in the order in which they are checked in "handleAccess".
		// If that order changes in that function, for readability, these should also be changed:
		[AP.actualizing]: ['activityName', 'locationId', 'customFields', 'roleId'],
		[AP.actualized]: [
			'liveDateRange',
			'roleId',
			'locationId',
			'activityName',
			'customFields',
			'liveCrewSize',
			'liveDurationDays',
		],
		[AP.planned]: [],
		[AP.plannedPreview]: [
			'liveDateRange',
			'roleId',
			'locationId',
			'activityName',
			'customFields',
			'liveCrewSize',
			'liveDurationDays',
		],
	},
	[TT.Milestone]: {
		// These are sorted in the order in which they are checked in "handleAccess".
		// If that order changes in that function, for readability, these should also be changed:
		[AP.actualizing]: ['roleId', 'locationId', 'customFields', 'activityName'],
		[AP.actualized]: ['liveDateRange', 'roleId', 'locationId', 'activityName', 'customFields'],
		[AP.plannedPreview]: ['liveDateRange', 'roleId', 'locationId', 'activityName', 'customFields'],
		[AP.planned]: [],
	},
	[TT.Constraint]: {
		// These are sorted in the order in which they are checked in "handleAccess".
		// If that order changes in that function, for readability, these should also be changed:
		[CAP.UNASSIGNED]: {
			[CFCS.REQUESTED_BY_MEMBER]: [],
			[CFCS.ASSIGNED_TO_MEMBER]: [],
			[CFCS.IS_CREATOR]: [],
			[CFCS.IS_ADMIN]: [],
		},
		[CAP.ASSIGNED]: {
			[CFCS.IS_ADMIN]: [],
			[CFCS.ASSIGNED_TO_MEMBER]: [
				'liveDateRange',
				'locationId',
				'requestedByProjectMemberId',
				'dateRequested',
				'dateRequestedAdapter',
				'activityName',
			],
			[CFCS.IS_CREATOR]: [],
			[CFCS.REQUESTED_BY_MEMBER]: ['liveDateRange', 'requestedByProjectMemberId'],
		},
		[CAP.ACCEPTED]: {
			[AP.actualizing]: [
				'locationId',
				'customFields',
				'activityName',
				'requestedByProjectMemberId',
				'dateRequested',
				'dateRequestedAdapter',
			],
			[AP.actualized]: [
				'liveDateRange',
				'locationId',
				'activityName',
				'customFields',
				'requestedByProjectMemberId',
				'liveAssignedProjectMemberId',
				'dateRequested',
				'dateRequestedAdapter',
			],
			[CFCS.IS_ADMIN]: ['dateRequested', 'dateRequestedAdapter'],
			[CFCS.ASSIGNED_TO_MEMBER]: [
				'liveDateRange',
				'locationId',
				'activityName',
				'requestedByProjectMemberId',
				'dateRequested',
				'dateRequestedAdapter',
			],
			[CFCS.IS_CREATOR]: [
				'liveDateRange',
				'locationId',
				'activityName',
				'customFields',
				'requestedByProjectMemberId',
				'liveAssignedProjectMemberId',
				'dateRequested',
				'dateRequestedAdapter',
			],
			[CFCS.REQUESTED_BY_MEMBER]: [
				'liveDateRange',
				'locationId',
				'activityName',
				'customFields',
				'requestedByProjectMemberId',
				'liveAssignedProjectMemberId',
				'dateRequested',
				'dateRequestedAdapter',
			],
		},
		[CAP.COMPLETE]: {
			[AP.actualizing]: [
				'locationId',
				'customFields',
				'activityName',
				'requestedByProjectMemberId',
				'dateRequested',
				'dateRequestedAdapter',
			],
			[AP.actualized]: [
				'liveDateRange',
				'locationId',
				'activityName',
				'customFields',
				'requestedByProjectMemberId',
				'liveAssignedProjectMemberId',
				'dateRequested',
				'dateRequestedAdapter',
			],
		},
		[CFCS.NO_ACCESS]: [
			'liveDateRange',
			'locationId',
			'activityName',
			'customFields',
			'requestedByProjectMemberId',
			'assignedProjectMemberId',
			'dateRequested',
			'dateRequestedAdapter',
		],
	},
};

const activeLineValidatorConfig: ActiveLineValidatorConfig = {
	comparisonFunction: (lineData, controlValue) => {
		if (!controlValue || controlValue.length === 0 || !lineData || !lineData.activeLineDate) {
			return true;
		}

		const start = controlValue[0]; //this can either have one value or two
		return start <= lineData.activeLineDate;
	},
	message: AppStrings.ACTIVE_LINE_START_DATE,
	messageHeader: AppStrings.ACTIVE_LINE_PIN_RIGHT_HEADER,
};

@Component({
	selector: 'ticket-edit',
	template: '',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketEditComponent implements OnInit, OnDestroy {
	// This Observable is ONLY meant to be used in single-edit cases.
	public editedTicketId$ = new Observable<string>();

	public saveCustomFieldsRequested$ = new Subject<void>();
	public cancelCustomFieldsRequested$ = new Subject<void>();
	private logger: ILoggerService;

	protected isWbsCodeEnabled$ = this.featureFlagManager.getFlag$(DynamicFlagTypes.TICKETS_WBS_ENABLED);

	public PlanAccessLevel = PlanAccessLevel;

	// Used to show/hide fields in the forms of various ticket types and modes (single/multi edit)
	public isAdmin$: Observable<boolean> = this.planAccessCheck.isAdmin$;

	public get isReadOnlyTicket() {
		if (!this.data || !this.data.data) {
			return true;
		}
		return !this.planAccessCheck.hasAccessToTicketSync(this.data.data); // || this.accessCheck.isReadOnly(); //MOC-4371 remove locked plan check
	}

	public get isFormValid() {
		return !this.editForm.invalid;
	}

	public get variancePromptOpen() {
		// this.logger.log('prompt open', this.editForm.contains('variance'))
		return this.editForm.contains('variance');
	}

	constructor(
		protected ticketEditService: TicketEditService,
		protected formBuilder: UntypedFormBuilder,
		protected planState: PlanState,
		protected cd: ChangeDetectorRef,
		protected constraintAcceptanceService: ConstraintAcceptanceService,
		protected planAccessCheck: PlanAccessCheck,
		protected featureFlagManager: FeatureFlagManager
	) {
		this.logger = LoggerServiceFactory.create('TicketEditComponent');
		// This emits unique changes to the ID of the ticket being edited.
		// This Observable is ONLY meant to be used in single-edit cases:
		this.editedTicketId$ = this.ticketEditService.combinedEdit$.pipe(
			tap((etc) => {
				if (etc.originalList.size > 1) {
					this.logger.warn("This code isn't meant to handle edits of more than one ticket");
				}
			}),
			map((etc) => (etc === null || etc.originalList.size > 1 ? null : etc.originalList.keys().next().value)), // null, id_1, id_2, id_3 ...
			distinctUntilChanged(),
			takeUntil(this.shutdownSubject)
		);

		this.dateRequestedDateAdapter = this.addDateFormControlAdapter(
			this.editForm,
			'dateRequested',
			'dateRequestedAdapter'
		);
	}

	private addDateFormControlAdapter(
		formGroup: UntypedFormGroup,
		nameOfStringFormControl: string,
		nameOfDateFormControl: string
	) {
		const newAdapter = new DateFormControlAdapter(formGroup.get(nameOfStringFormControl));
		formGroup.addControl(nameOfDateFormControl, newAdapter.dateFormControl);
		return newAdapter;
	}

	//not sure about these, it may be easier as far as the forms go to compose the
	// enabled and disabled state directly into the form on combinedEdit emission
	// ...you know what, that is better, these can stay as "maybe" things though
	get isPlanned() {
		return this.data.data.actualPhase === AP.planned;
	}
	get isPlannedPreview() {
		return this.data.data.actualPhase === AP.plannedPreview;
	}
	get isActualizing() {
		return this.data.data.actualPhase === AP.actualizing;
	}
	get isActualized() {
		return this.data.data.actualPhase === AP.actualized;
	}
	public shutdownSubject = new Subject<void>();
	public hasAccess$: Observable<any>;
	public planAccess$: Observable<PlanAccessLevel>;
	public calendarController$: Observable<boolean>;
	public projectMember$: Observable<any>; // used to filter role / project members in the respective drop down fields
	public projectMemberList$: Observable<Map<string, ProjectMember>>; // Used for filtering Requested By dropdown

	public activeLine$ = this.planState.plan.object$.pipe(
		map((plan) => {
			if (!plan) {
				return null;
			}
			return { activeLineDate: plan.view.activeLineDate };
		}),
		distinctUntilChanged(),
		takeUntil(this.shutdownSubject)
	);

	public makeShake = new Subject();
	private projectType: string;

	private metadataObservables = this.requiredValidationSetup();

	//new stuff
	public data: EditTicketsCombined;

	//if it turns out we need a more specific set of validators, define the base set
	// of validators in a different config object. One that can be queried and manipulated.
	// Then that one can be used as a source to rebuild the validators for each type
	// with minimal duplication, since angular doesn't really allow manipulating
	// the validator list beyond replacing the whole thing.
	public editForm = this.formBuilder.group({
		activityName: [
			'',
			{
				validators: [
					requiredOrMulti(
						this.metadataObservables['activityName'],
						AppStrings.TICKET_ERRORS_DESCRIPTION_REQUIRED_HEADER,
						AppStrings.TICKET_ERRORS_DESCRIPTION_REQUIRED_MESSAGE
					),
					// validatorDecorator(Validators.required, AppStrings.TICKET_ERRORS_DESCRIPTION_REQUIRED_HEADER, AppStrings.TICKET_ERRORS_DESCRIPTION_REQUIRED_MESSAGE)
				],
			},
		], //activityName: ['', testValidator()],
		liveAssignedProjectMemberId: [
			'',
			{
				validators: [
					requiredOrMulti(
						this.metadataObservables['liveAssignedProjectMemberId'],
						AppStrings.TICKET_ERRORS_ASSIGNED_TO_REQUIRED_HEADER,
						AppStrings.TICKET_ERRORS_ASSIGNED_TO_REQUIRED_MESSAGE
					),
				],
			},
		],
		requestedByProjectMemberId: [
			'',
			{
				validators: [requiredOrMulti(this.metadataObservables['requestedByProjectMemberId'])],
			},
		],
		liveCrewSize: [
			'',
			{
				validators: [
					requiredOrMulti(
						this.metadataObservables['liveCrewSize'],
						AppStrings.TICKET_ERRORS_CREW_REQUIRED_HEADER,
						AppStrings.TICKET_ERRORS_CREW_SIZE_MIN_MAX_MESSAGE(
							ticketValidator['liveCrewSize'].min,
							ticketValidator['liveCrewSize'].max
						)
					),
					greaterThanValidator(ticketValidator.liveCrewSize.min),
					validatorDecorator(
						Validators.pattern(ticketValidator.liveDurationDays.regexPattern),
						AppStrings.TICKET_ERRORS_CREW_SIZE_WHOLE_HEADER,
						AppStrings.TICKET_ERRORS_CREW_SIZE_MIN_MAX_MESSAGE(
							ticketValidator['liveCrewSize'].min,
							ticketValidator['liveCrewSize'].max
						)
					),
					validatorDecorator(
						Validators.max(ticketValidator.liveCrewSize.max),
						AppStrings.TICKET_ERRORS_CREW_SIZE_TOO_LENGTHY_HEADER,
						AppStrings.TICKET_ERRORS_CREW_SIZE_MIN_MAX_MESSAGE(
							ticketValidator['liveCrewSize'].min,
							ticketValidator['liveCrewSize'].max
						)
					),
				],
			},
		],
		liveDurationDays: [
			'',
			{
				validators: [
					requiredOrMulti(
						this.metadataObservables['liveDurationDays'],
						AppStrings.TICKET_ERRORS_DURATION_REQUIRED_HEADER,
						AppStrings.TICKET_ERRORS_DURATION_MIN_MAX_MESSAGE(
							ticketValidator['liveDurationDays'].min,
							ticketValidator['liveDurationDays'].max
						)
					),
					greaterThanValidator(ticketValidator.liveDurationDays.min),
					validatorDecorator(
						Validators.pattern(ticketValidator.liveDurationDays.regexPattern),
						AppStrings.TICKET_ERRORS_DURATION_WHOLE_HEADER,
						AppStrings.TICKET_ERRORS_DURATION_MIN_MAX_MESSAGE(
							ticketValidator['liveDurationDays'].min,
							ticketValidator['liveDurationDays'].max
						)
					),
					validatorDecorator(
						Validators.max(ticketValidator.liveDurationDays.max),
						AppStrings.TICKET_ERRORS_DURATION_TOO_LENGTHY_HEADER,
						AppStrings.TICKET_ERRORS_DURATION_MIN_MAX_MESSAGE(
							ticketValidator['liveDurationDays'].min,
							ticketValidator['liveDurationDays'].max
						)
					),
				],
			},
		], //https://angular.io/api/forms/AbstractControlOptions
		locationId: [''],
		roleId: [
			'',
			{
				validators: [requiredOrMulti(this.metadataObservables['roleId'])],
			},
		],
		liveDateRange: [
			[],
			{
				validators: [dateRangeStartRequiredValidator()],
				asyncValidators: [activeLineSideValidator(this.activeLine$, activeLineValidatorConfig)],
			},
		],
		liveWorkingDays: [''],
		dateRequested: [
			'',
			{
				validators: [
					// validatorDecorator(Validators.required, AppStrings.TICKET_ERRORS_REQUIRED_DATE_REQUESTED_HEADER, AppStrings.TICKET_ERRORS_REQUIRED_DATE_REQUESTED_MESSAGE)
					requiredOrMulti(
						this.metadataObservables['dateRequested'],
						AppStrings.TICKET_ERRORS_REQUIRED_DATE_REQUESTED_HEADER,
						AppStrings.TICKET_ERRORS_REQUIRED_DATE_REQUESTED_MESSAGE
					),
				],
			},
		],
		externalScheduleId: [''],
		wbsCode: [''],
		customFields: this.formBuilder.group({}),
		milestoneType: [''],
	});

	private dateRequestedDateAdapter: DateFormControlAdapter;

	public customFieldsListFormGroup = this.editForm.get('customFields') as UntypedFormGroup;

	//special form control to be added and removed
	public varianceControl = new UntypedFormControl('', { validators: [varianceFormDataValidator()] });
	public variancePromptRemoved$ = new Subject();

	//temporary fix to refocus a ticket whenever it's position changes
	// this will break hardcore when we support dragging your ticket in edit
	// but I _highly_ suspect that focus everything behavior will need to go away then
	// so... temp fix
	private previousLeft = 0;
	private previousWidth = 0;
	private previousTop = 0;

	public removeVarianceControl() {
		this.editForm.removeControl('variance');
		this.variancePromptRemoved$.next(true);
		this.varianceControl.setValue('');
	}

	// dirtyChanged = this.editForm.statusChanges.pipe(
	// 	mapTo(this.editForm.dirty),
	// 	distinctUntilChanged()
	// );

	public updateActualPhase(phase: ActualPhase) {
		if (phase === AP.planned) {
			this.ticketEditService.transitionToPlanned(this.data);
		} else if (phase === AP.actualizing) {
			if (this.editForm.invalid) {
				if (this.editForm.contains('variance') && this.varianceControl.invalid) {
					this.planState.makeVariancePromptShake();
					// this.makeShake.next();
				}
				return;
			} else if (this.editForm.contains('variance')) {
				this.removeVarianceControl();
			}
			this.ticketEditService.transitionToActualizing(this.data);
			this.ticketEditService.markFormAsDirty();

			// checking size is probably an overkill here... Update Status is currently used for single ticket edit only
			if (this.data.selectedList.size > 0) {
				const tickets = Array.from(this.data.selectedList.values());
				for (const ticket of tickets) {
					if (ticket.database.isActualized !== ticket.view.isActualized) {
						// Note the change as a segment event:
						analyticsEventsService.updateStatusButtonClicked(this.planState.planId, ticket.$id);
					}
				}
			}
		} else if (phase === AP.plannedPreview) {
			this.ticketEditService.transitionToPlannedPreview(this.data);
		} else if (phase === AP.actualized) {
			this.ticketEditService.transitionToActualized(this.data);
		} else {
			this.logger.log('combination not yet implemented: ', phase);
		}
	}
	private needsFocus(data: EditTicketsCombined) {
		if (!data || data.selectedList.size === 0) {
			return;
		}
		if (!data.ticketsInPlanRegion) {
			return;
		}
		if (
			data.data.left !== this.previousLeft ||
			data.data.width !== this.previousWidth ||
			data.data.top !== this.previousTop
		) {
			this.ticketEditService.focusTicket(data);
		}
		this.previousLeft = data.data.left;
		this.previousWidth = data.data.width;
		this.previousTop = data.data.top;
	}

	private requiredValidationSetup(): { [key: string]: Observable<string> } {
		//keyMetadata observables
		const requiredKeys = [
			'activityName',
			'liveCrewSize',
			'liveDurationDays',
			'roleId',
			'dateRequested',
			'dateRequestedAdapter',
			'liveAssignedProjectMemberId',
			'requestedByProjectMemberId',
		];
		const thing = this.ticketEditService.combinedEdit$.pipe(
			map((editData) => {
				if (!editData) {
					return EMPTY;
				}
				const obj = {};
				requiredKeys.map((key) => (obj[key] = editData.keyMetadata.get(key)));
				return obj;
			})
		);

		const list = pluckList(thing, requiredKeys);

		Object.keys(list).forEach((key) => {
			list[key] = list[key].pipe(takeUntil(this.shutdownSubject));
		});
		// for(var key in list){
		// 	((key)=>{
		// 		list[key] = list[key].pipe(tap(thing => this.logger.log('validator test', key, thing)),takeUntil(this.shutdownSubject));
		// 	})(key)
		// }
		return list;
	}

	public ngOnInit() {
		// this.createForm();
		// this.logger.log('-------- onInit');

		// Automatically opens the calendar with "Update Status" is clicked
		this.calendarController$ = this.ticketEditService.combinedEdit$.pipe(
			pluck('data', 'actualPhase'),
			distinctUntilChanged(),
			map((phase: ActualPhase) => {
				return phase === AP.actualizing;
			}),
			takeUntil(this.shutdownSubject)
		);

		this.ticketEditService.combinedEdit$
			.pipe(startWith(null as EditTicketsCombined), pairwise(), takeUntil(this.shutdownSubject))
			.subscribe((lastTwo) => {
				const edit = lastTwo[1]; //the current value
				this.data = edit;
				// this.logger.log('---------- edit update ------------ last internal:', edit.lastUpdateInternal);

				//in theory this isn't _necessary_ but not including this self check will result in rewriting the form value from data
				if (!edit.lastUpdateInternal) {
					this.editForm.patchValue(edit.data, { emitEvent: false });

					// We also need to update the Date-valued part of the 'dateRequestedAdapter':
					this.dateRequestedDateAdapter.manualDateUpdate(edit.data.dateRequested, false);

					if (edit.data.liveLastPromise && edit.data.liveLastPromise.data) {
						const promise = edit.data.liveLastPromise.data;
						this.varianceControl.setValue({
							varianceId: promise.varianceId || null,
							varianceComment: promise.varianceComment || null,
						});
					}
				}
				// if(this.data.ticketJustAdded){ this.editForm.markAsDirty(); }
				const specialNeedsVariance =
					lastTwo[0] &&
					edit.data.isActualized &&
					edit.data.liveFinish !== lastTwo[0].data.liveFinish &&
					lastTwo[0].data.actualPhase !== AP.plannedPreview; //special case it when the last one was plannedPreview MOC-4053

				//add the variance control if it needs to be, and hasn't been
				if (
					(this.data.data.needsVariance || specialNeedsVariance) &&
					!this.editForm.contains('variance') &&
					this.ticketEditService.editOverlay$.getValue() !== 'pinHistory'
				) {
					this.editForm.addControl('variance', this.varianceControl);
					this.editForm.controls['variance'].valueChanges
						.pipe(takeUntil(merge(this.variancePromptRemoved$, this.shutdownSubject)))
						.subscribe((variance) => {
							this.varianceFormUpdated(variance);
						});
				}

				// MOC-4273
				this.planAccessCheck.hasRestrictedAccess$
					.pipe(skip(1), takeUntil(this.shutdownSubject))
					.subscribe((isReadOnly) => {
						if (isReadOnly) {
							window.Logging.warning(AppStrings.EDIT_PLAN_LOCKED_WHILE_EDITING);
						}
					});

				//remove the variance control if something external cleared the need for variance
				// and there's no variance (allows the control to stay open while the user is editing it)
				const doesNotHaveVariance = !getNested(this.data.data, 'liveLastPromise.data.varianceId');
				if (this.editForm.contains('variance') && !this.data.data.needsVariance && doesNotHaveVariance) {
					this.removeVarianceControl();
				}

				this.needsFocus(edit);
				this.handleAccess();
				this.updateMessageString();
				this.cd.markForCheck();
				window.ticketEdit = this;
			});

		// Handles generic access to ticket based on role/admin.
		// Specific access is done by ticket type in the handleAccess() method
		this.hasAccess$ = this.planState.authedProjectMember$.pipe(
			map((youThing) => {
				if (!youThing) {
					return false;
				}
				const hasAccessToTicket = this.planAccessCheck.hasAccessToTicketSync(this.data.data);
				// this.handleAccess();
				return hasAccessToTicket;
			}),
			takeUntil(this.shutdownSubject)
		);

		// expose the plan access level to ticket-edit components
		this.planAccess$ = this.planAccessCheck.accessLevel$;

		// Subscribe to all project members in current project
		this.projectMemberList$ = this.planState.projectMembersApi.data.list$.pipe(
			takeUntil(this.shutdownSubject)
		);

		// Passes in the project member data as input for relevant components
		this.projectMember$ = this.planState.authedProjectMember$.pipe(takeUntil(this.shutdownSubject));

		// MOC-3947 - Special case where High Rise sample project says read only
		this.planState.project.object$
			.pipe(pluck('projectType'), takeUntil(this.shutdownSubject))
			.subscribe((type: string) => {
				this.projectType = type;
				this.updateMessageString();
			});

		this.setupFormSubscription(this.editForm);

		// MOC-3314 - Register this form to the ticketEditService
		this.ticketEditService.registerForm(this.editForm);

		//also update the message string when this changes
		this.variancePromptRemoved$.pipe(takeUntil(this.shutdownSubject)).subscribe(() => {
			this.updateMessageString();
		});

		// The TicketEditService acts as a "broker" for implicit saves, when the cycle tickets UI
		// switches the ticket being edited and that requires an implicit save of the currently
		// edited ticket's custom fields:
		this.ticketEditService?.externalSaveCustomFieldsRequest$
			.pipe(takeUntil(this.shutdownSubject))
			.subscribe(() => {
				this.saveCustomFields();
			});
	}

	// MOC-3259. Handling the update of the message string here so we have access to the editForm
	private updateMessageString() {
		if (!this.data) {
			return;
		}

		let childDirty = false;
		for (const key in this.editForm.controls) {
			if (this.editForm.controls[key].dirty && this.editForm.controls[key].invalid) {
				childDirty = true;
			}
		}

		if (this.data.ticketJustAdded && !childDirty) {
			// Brand new ticket
			this.setMessageString(AppStrings.EDIT_TOP_BAR_NEW_TICKET);
			// } else if (this.data.data.needsVariance){ // Instructions on how to set variance
		} else if (this.editForm.contains('variance')) {
			// Instructions on how to set variance
			this.setMessageString(AppStrings.EDIT_TOP_BAR_VARIANCE_NEEDED);
		} else if (this.projectType === 'sampleProjectShared') {
			// Special handling for 'High Rise' project
			this.setMessageString(AppStrings.EDIT_TOP_BAR_READ_ONLY_SAMPLE_PROJECT_SHARED);
		} else if (
			this.planAccessCheck.hasRestrictedAccessSync &&
			!this.data.isAnyConstraint(this.data.selectedType)
		) {
			// Plan is locked and is not a constraint
			this.setMessageString(AppStrings.EDIT_TOP_BAR_PLAN_LOCKED);
		} else if (
			this.planAccessCheck.hasRestrictedAccessSync &&
			this.data.isAnyConstraint(this.data.selectedType)
		) {
			// Plan is locked and is a constraint
			this.setMessageString(AppStrings.EDIT_TOP_BAR_PLAN_LOCKED_CONSTRAINT);
		} else if (this.editForm.invalid) {
			// If ticket has errors
			this.setMessageString(AppStrings.EDIT_TOP_BAR_TICKET_ERRORS);
		} else if (this.data.data.actualPhase === AP.actualizing) {
			// Instructions on how to update
			this.data.data.type === TT.Constraint || this.data.data.type === TT.Milestone
				? this.setMessageString(AppStrings.EDIT_TOP_BAR_UPDATE_STATUS_SPECIAL_TICKET)
				: this.setMessageString(AppStrings.EDIT_TOP_BAR_UPDATE_STATUS);
		} else if (!this.planAccessCheck.hasAccessToTicketSync(this.data.data)) {
			this.setMessageString(AppStrings.EDIT_TOP_BAR_READ_ONLY);
		} else if (this.data.data.actualPhase === AP.actualized && this.data.selectedType === TT.Constraint) {
			this.setMessageString(AppStrings.EDIT_TOP_BAR_ACTUALIZED_CONSTRAINT);
		} else if (this.data.data.actualPhase === AP.actualized) {
			this.setMessageString(AppStrings.EDIT_TOP_BAR_ACTUALIZED);
		} else if (this.data.data.actualPhase === AP.plannedPreview) {
			this.setMessageString(AppStrings.EDIT_TOP_BAR_VIEWING_PLANNED_PREVIEW);
		} else if (this.data.data.type === TT.Constraint && this.data.data.constraintState === CAP.ASSIGNED) {
			this.setMessageString(AppStrings.EDIT_TOP_BAR_CONSTRAINT_PENDING_ACCEPTANCE);
		} else if (this.data.data.type === TT.Constraint && this.data.data.constraintState === CAP.ACCEPTED) {
			this.setMessageString(AppStrings.EDIT_TOP_BAR_CONSTRAINT_ACCEPTED);
		} else if (this.data.data.type === TT.Constraint) {
			// MOC-3965 - Special override for constraints
			this.setMessageString(AppStrings.EDIT_TOP_BAR_EDIT_TICKET_CONSTRAINT);
		} else if (this.data.data.side === Side.PULL) {
			this.setMessageString(AppStrings.EDIT_TOP_BAR_REVISE_TICKET_PULL_SIDE);
		} else {
			this.setMessageString(AppStrings.EDIT_TOP_BAR_EDIT_TICKET);
		}
	}

	private handleAccess(): void {
		// Make things a little less verbose, and try to solve tslint-long-line issues:
		const d = this.data;
		const dd = d.data;
		if (d.selectedList.size > 1) {
			d.selectedList.forEach((key) => {
				if (
					!this.planAccessCheck.hasAccessToTicketSync(d.selectedList.get(key.$id).view) &&
					(d.selectedType !== TT.Constraint ||
						!this.planAccessCheck.pmMatchesSync(d.selectedList.get(key.$id).view.creatorId))
				) {
					return this.disableAllFormControls();
				}

				//block readonly editing unless actualizing
				if (
					this.planAccessCheck.hasRestrictedAccessSync &&
					dd.actualPhase !== AP.actualizing &&
					d.ticketsInPlanRegion
				) {
					return this.disableAllFormControls();
				}

				switch (dd.type) {
					// Handle Milestone separate and not in 'default' because a task can be either undefined or 'task'. Probably cleaner to keep separate
					case TT.Milestone:
						this.handleFormControlDisableState(FormControlsToDisable[TT.Milestone][dd.actualPhase]);
						break;
					case TT.Constraint:
						this.handleConstraintDisableState(dd);
						break;
					default:
						this.handleFormControlDisableState(FormControlsToDisable[TT.Task][dd.actualPhase]);
						break;
				}
			});
		} else {
			if (
				!this.planAccessCheck.hasAccessToTicketSync(dd) &&
				(dd.type !== TT.Constraint || !this.planAccessCheck.pmMatchesSync(dd.creatorId))
			) {
				return this.disableAllFormControls();
			}

			//block readonly editing unless actualizing
			if (
				this.planAccessCheck.hasRestrictedAccessSync &&
				dd.actualPhase !== AP.actualizing &&
				d.ticketsInPlanRegion
			) {
				return this.disableAllFormControls();
			}
			switch (dd.type) {
				// Handle Milestone separate and not in 'default' because a task can be either undefined or 'task'. Probably cleaner to keep separate
				case TT.Milestone:
					this.handleFormControlDisableState(FormControlsToDisable[TT.Milestone][dd.actualPhase]);
					break;
				case TT.Constraint:
					this.handleConstraintDisableState(dd);
					break;
				default:
					this.handleFormControlDisableState(FormControlsToDisable[TT.Task][dd.actualPhase]);
					break;
			}
		}
	}

	private handleConstraintDisableState(dd: EditCombinedData) {
		if (!dd.constraintState) {
			this.enableAllFormControls();
		} else if (dd.actualPhase === AP.plannedPreview) {
			this.disableAllFormControls();
		} else if (dd.actualPhase === AP.actualizing || dd.actualPhase === AP.actualized) {
			this.handleFormControlDisableState(
				FormControlsToDisable[TT.Constraint][dd.constraintState][dd.actualPhase]
			);
		} else {
			const isAdmin: boolean = this.planAccessCheck.isAdminSync;
			const isAssignedTo: boolean = this.assignedTo(dd);
			const isCreator: boolean = this.planAccessCheck.pmMatchesSync(dd.creatorId);
			const isRequestedBy: boolean = this.planAccessCheck.pmMatchesSync(dd.requestedByProjectMemberId);
			if (isAdmin) {
				this.handleFormControlDisableState(
					FormControlsToDisable[TT.Constraint][dd.constraintState][CFCS.IS_ADMIN]
				);
			} else if (isAssignedTo) {
				// For Edited/Created Constraints by a non Admin User, and assigned it to themselves,
				// Only deactivate form fields if the ticket is in the plan region,
				// otherwise fields should be editable and validated.
				const formControlsToDisable = this.data.ticketsInPlanRegion
					? FormControlsToDisable[TT.Constraint][dd.constraintState][CFCS.ASSIGNED_TO_MEMBER]
					: [];
				this.handleFormControlDisableState(formControlsToDisable);
			} else if (isCreator) {
				this.handleFormControlDisableState(
					FormControlsToDisable[TT.Constraint][dd.constraintState][CFCS.IS_CREATOR]
				);
			} else if (isRequestedBy) {
				this.handleFormControlDisableState(
					FormControlsToDisable[TT.Constraint][dd.constraintState][CFCS.REQUESTED_BY_MEMBER]
				);
			} else {
				this.handleFormControlDisableState(FormControlsToDisable[TT.Constraint][CFCS.NO_ACCESS]);
			}
		}
	}

	private assignedTo(data: EditCombinedData): boolean {
		const assignedToMemberId = data.responsibleProjectMemberId
			? data.responsibleProjectMemberId
			: data.assignedProjectMemberId;
		return this.planAccessCheck.pmMatchesSync(assignedToMemberId);
	}

	private handleFormControlDisableState(disabledList: string[]): void {
		if (!disabledList) {
			return;
		}
		this.enableAllFormControls();
		disabledList.forEach((formId: string) => {
			const form = this.editForm.get(formId);
			if (form) {
				form.disable({ emitEvent: false });
			}
		});

		//enable/ disable liveDateRange based on pull/ not pull side
		const range = this.editForm.get('liveDateRange');
		if (this.data.databaseSide === Side.PULL && !range.disabled) {
			range.disable({ emitEvent: false });
		} else if (this.data.databaseSide !== Side.PULL && range.disabled) {
			range.enable({ emitEvent: false });
		}
	}

	private enableAllFormControls() {
		for (const key of Object.keys(this.editForm.controls)) {
			const form = this.editForm.get(key);
			if (form) {
				form.enable({ emitEvent: false });
			}
		}
	}

	private disableAllFormControls() {
		for (const key of Object.keys(this.editForm.controls)) {
			const form = this.editForm.get(key);
			if (form) {
				form.disable({ emitEvent: false });
			}
		}
	}

	//TODO - massive clean up, less hard coded keys, etc
	public setupFormSubscription(form: UntypedFormGroup) {
		const specialBehaviors = {
			liveDurationDays: 'liveDurationDays',
			liveDateRange: 'liveDateRange',
			liveWorkingDays: 'liveWorkingDays',
			liveAssignedProjectMemberId: 'liveAssignedProjectMemberId',
			requestedByProjectMemberId: 'requestedByProjectMemberId',
			variance: 'variance', //don't actually handle it here, just ignore it
			dateRequestedAdapter: 'dateRequestedAdapter', //don't actually handle it here, just ignore it
			roleId: 'roleId',
		};
		const simpleUpdateInited = [];
		/** used to determine the first emission */
		const simpleUpdate = [];

		// tslint:disable-next-line: forin
		for (const key in form.controls) {
			if (specialBehaviors[key]) {
				form
					.get(key)
					.valueChanges.pipe(takeUntil(this.shutdownSubject))
					.subscribe((data) => {
						switch (key) {
							case 'liveDurationDays':
								this.durationFormUpdated(data);
								break;
							case 'liveDateRange':
								this.dateRangeFormUpdated(data);
								break;
							case 'liveWorkingDays':
								this.workingDaysFormUpdated(data);
								break;
							case 'liveAssignedProjectMemberId':
								this.liveAssignedProjectMemberIdUpdated(data);
								break;
							case 'requestedByProjectMemberId':
								this.requestedByProjectMemberIdUpdated(data);
								break;
							case 'roleId':
								this.roleIdUpdated(data);
								break;
						}
					});
			} else if (key !== 'customFields') {
				simpleUpdateInited.push(
					form.get(key).valueChanges.pipe(
						startWith(form.get(key).value),
						map((t) => ({ key, val: t }))
					)
				);
				simpleUpdate.push(form.get(key).valueChanges);
				// simpleUpdate.push( form.get(key).valueChanges.pipe(map(t => { return {key:key, val:t} })) )
			}
		}

		const start$ = merge(...simpleUpdate).pipe(take(1));

		combineLatest(simpleUpdateInited)
			.pipe(
				skipUntil(start$),
				map((list) => {
					const thing: any = {};
					list.forEach((item: any) => (thing[item.key] = item.val));
					return thing;
				}),
				takeUntil(this.shutdownSubject)
			)
			.subscribe((t) => {
				this.simpleFormUpdate(t);
			});
	}

	public setMessageString(message: string) {
		this.data.setMessageString(message);
	}

	//form updated event handling
	public simpleFormUpdate(formData) {
		// this.logger.log('SIMPLE FORM UPDATE', formData);
		this.ticketEditService.writeCombinationBackToList(this.data, formData);
	}
	public durationFormUpdated(duration) {
		// this.logger.log('DURATION FORM UPDATE', duration);
		this.ticketEditService.setDurationFromForm(this.data, duration);
	}
	public varianceFormUpdated(varianceData: VarianceFormData) {
		this.ticketEditService.setCollectedVariance(
			this.data,
			varianceData.varianceId,
			varianceData.varianceComment
		);
	}
	public dateRangeFormUpdated(dateRange) {
		//this.logger.log('DATE RANGE FROM FORM UPDATE', dateRange);
		const dateObj = {
			liveStart: dateRange[0],
			liveFinish: dateRange[1],
		};
		this.ticketEditService.setDateRangeFromForm(this.data, dateObj);
	}
	public workingDaysFormUpdated(workingDays) {
		// this.logger.log('combine workingDays test', workingDays);
		this.ticketEditService.setWorkdaysFromForm(this.data, workingDays);
		// this.ticketEditService.writeCombinationBackToList(this.data, {liveWorkingDays: workingDays});
	}

	public liveAssignedProjectMemberIdUpdated(newAssignedProjectMemberId: string) {
		if (
			this.data.data.liveAssignedProjectMemberId &&
			this.data.ticketsInPlanRegion &&
			this.data.data.constraintState === CAP.ACCEPTED
		) {
			this.editForm.get('requestedByProjectMemberId').setValue(this.data.data.liveAssignedProjectMemberId);
		}
		this.constraintAcceptanceService.setAssignedProjectMember(this.data, newAssignedProjectMemberId, true);
	}

	public requestedByProjectMemberIdUpdated(newRequestedByProjectMemberId: string) {
		this.constraintAcceptanceService.setRequestedByProjectMemberId(this.data, newRequestedByProjectMemberId);
	}

	public roleIdUpdated(newRoleId: string) {
		this.ticketEditService.setRoleIdFromForm(this.data, newRoleId);
	}

	public variancePromptSaved(varianceData: VarianceFormData) {
		// this.ticketEditService.setCollectedVariance(this.data, varianceData.varianceId, varianceData.varianceComment);
		this.removeVarianceControl();
	}
	public variancePromptCanceled() {
		Logging.notice(
			'unsure what to make this do, seems like it should probably behave like an undo... But we lack that infrastructure'
		);
	}

	protected saveCustomFields(): void {
		this.saveCustomFieldsRequested$.next();
	}

	public save(): void {
		if (this.editForm.invalid) {
			for (const key of Object.keys(this.editForm.controls)) {
				this.editForm.controls[key].markAsDirty();
			}
			this.editForm.markAsDirty();
			return;
		}

		this.saveCustomFields();
		this.ticketEditService.saveEditAndClose();
	}

	public cancel(): void {
		this.cancelCustomFieldsRequested$.next();
		this.ticketEditService.closeEdit();
	}

	public ngOnDestroy() {
		this.shutdownSubject.next();
		this.ticketEditService.clearForm();
	}

	public clearInput(dataKey: string, formKey?: string) {
		if (!formKey) {
			formKey = dataKey;
		}
		const cont = this.editForm.controls[formKey];
		cont.markAsDirty();
		cont.markAsTouched();
		cont.setValue(null);

		this.ticketEditService.clearInput(this.data.selectedList, dataKey);
	}

	public openPinHistory(event): void {
		this.ticketEditService.setOverlay(EditOverlay.pinHistory);
	}
}

// form modifications by ticket types
export function setupFormForTask(editForm: UntypedFormGroup) {
	editForm.removeControl('dateRequested');
	editForm.removeControl('dateRequestedAdapter');
	editForm.removeControl('liveAssignedProjectMemberId');
	editForm.removeControl('requestedByProjectMemberId');
	editForm.removeControl('milestoneType');
}
export function setupFormForMilestone(editForm: UntypedFormGroup) {
	editForm.removeControl('dateRequested');
	editForm.removeControl('dateRequestedAdapter');
	editForm.removeControl('liveDurationDays');
	editForm.removeControl('liveCrewSize');
	editForm.removeControl('liveAssignedProjectMemberId');
	editForm.removeControl('requestedByProjectMemberId');
	editForm.removeControl('liveWorkingDays');
}
export function setupFormForConstraint(editForm: UntypedFormGroup) {
	editForm.removeControl('roleId');
	editForm.removeControl('liveDurationDays');
	editForm.removeControl('liveCrewSize');
	editForm.removeControl('liveWorkingDays');
	editForm.removeControl('milestoneType');
}
